import { Form, FormRow } from '@chargeafter/forms';
import { TextField, Typography, VerticalSpace } from '@chargeafter/ui';
import { Box, Dialog, Grid, keyframes, styled } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { FC, useState, useEffect, useLayoutEffect } from 'react';
import { Keyboard } from './Keyboard';
import useFeedbackSnackbar from '../hooks/useFeedbackSnackbar';
import { useSetPinScreenOpen, useVerifyPIN } from '../services/pinManager';
import {
  AUTHORIZATION_SCREEN_DESC,
  AUTHORIZATION_SCREEN_HEADER,
  AUTHORIZATION_SCREEN_WARNING,
} from '../texts';
import { useSetPageName } from '../services/flowManager';
import { AppView } from '../constants';
import { FEEDBACK_TYPE } from '../services/feedbackManager';

type AuthorizationScreenProps = {
  open: boolean;
};

const Container = styled(Grid)({
  height: '100%',
  width: '100%',
  '& > *': {
    width: '100%',
    height: '50%',
  },
});

const StyledHeader = styled(Typography)(({ theme }) => ({
  color: theme.palette.secondary.main,
  textTransform: 'uppercase',
  fontSize: '18px',
  lineHeight: '18px',
}));

const pinFailAnimation = keyframes`
    0% { transform: translate(0, 0) },
    16.6% { transform: translate(10px, 0) },
    33.2% { transform: translate(-10px, 0) },
    49.8% { transform: translate(10px, 0) },
    66.4% { transform: translate(-10px, 0) },
    83% { transform: translate(10px, 0) },
    100% { transform: translate(-10px, 0) },
`;

const PIN_LENGTH = 4;
export const AuthorizationScreen: FC<AuthorizationScreenProps> = ({ open }) => {
  const setPageName = useSetPageName();
  const [enteredPIN, setEnteredPIN] = useState<string[]>(
    Array(PIN_LENGTH).fill(''),
  );
  const [feedback, closeSnackbar] = useFeedbackSnackbar();
  const [error, setError] = useState(false);
  const setPinScreenOpen = useSetPinScreenOpen();
  const verifyPIN = useVerifyPIN();

  useEffect(() => {
    setPageName(AppView.Authorization);
  }, []);

  const handleKeyClick = (keyValue: string) => {
    if (error) {
      closeSnackbar();
      setError(false);
    }
    const netEnteredPINLength = enteredPIN.join('').length;
    switch (keyValue) {
      case 'delete':
        if (netEnteredPINLength) {
          enteredPIN[netEnteredPINLength - 1] = '';
          setEnteredPIN([...enteredPIN]);
        }
        break;
      default:
        if (netEnteredPINLength < PIN_LENGTH) {
          enteredPIN[netEnteredPINLength] = keyValue;
          setEnteredPIN([...enteredPIN]);
        }
    }
  };

  useLayoutEffect(() => {
    if (open) {
      setEnteredPIN(Array(PIN_LENGTH).fill(''));
    }
  }, [open]);

  useEffect(() => {
    (async () => {
      if (enteredPIN.join('').length === PIN_LENGTH) {
        const verified = await verifyPIN(enteredPIN.join(''));
        if (verified) {
          setPinScreenOpen(false);
          closeSnackbar();
          setError(false);
        } else {
          feedback({
            type: FEEDBACK_TYPE.PIN_INCORRECT_FEEDBACK,
            isPersistent: true,
          });
          setEnteredPIN(Array(PIN_LENGTH).fill(''));
          setError(true);
        }
      }
    })();
  }, [enteredPIN]);

  return (
    <Dialog open={open} fullScreen>
      <Container container>
        <Grid item display="flex" alignItems="center" justifyContent="center">
          <Box width={(theme) => theme.spacing(36)}>
            <StyledHeader variant="h3" dataTestId="pin-screen-header">
              {AUTHORIZATION_SCREEN_HEADER}
            </StyledHeader>
            <Typography
              variant="body1"
              textAlign="center"
              padding={3}
              dataTestId="pin-screen-description"
            >
              {AUTHORIZATION_SCREEN_DESC}
            </Typography>
            <Box>
              <Form
                onSubmit={() => {}}
                render={() => (
                  <FormRow>
                    {enteredPIN.map((_, i) => (
                      <TextField
                        key={`code-${i}`}
                        name={`code-${i}`}
                        disabled
                        value={enteredPIN[i]}
                        onChange={() => {}}
                        type="password"
                        inputProps={{
                          'data-test-id': `pin-code-${i}`,
                        }}
                        sx={(theme) => ({
                          width: theme.spacing(6),
                          '& .Mui-disabled': {
                            '-webkit-text-fill-color': `${theme.palette.common.black} !important`,
                            textAlign: 'center',
                            outlineColor: error ? 'red' : 'initial',
                          },
                          fieldset: {
                            borderColor: error
                              ? 'red !important'
                              : 'initial !important',
                          },
                          animation: error
                            ? `${pinFailAnimation} ease .5s`
                            : 'none',
                        })}
                      />
                    ))}
                  </FormRow>
                )}
              />
            </Box>
            <VerticalSpace units={4} />
            <Box display="flex" width={(theme) => theme.spacing(40)}>
              <ErrorOutlineIcon color="inherit" />
              <Typography
                fontSize="16px"
                variant="warning"
                sx={{
                  color: 'inherit',
                }}
                dataTestId="pin-screen-warning"
              >
                {AUTHORIZATION_SCREEN_WARNING}
              </Typography>
            </Box>
          </Box>
        </Grid>
        <Keyboard handleKeyClick={handleKeyClick} />
      </Container>
    </Dialog>
  );
};
